/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼詳細サイドの活動ログカード。起票・ステータス / 担当 / 緊急度の変更・エスカレーション・差戻し・依頼内容の編集を時系列で表示する。既定では最新数件だけを出して隣の SLA カードと同じ高さに収め、それより多いときはトグルで全件へ展開する
 * @graph-connects opsActivityTimeLabel [calls] 各エントリの表示時刻を JST へ整形
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { opsActivityTimeLabel } from '@cortex/ops-core';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import type { OpsActivity } from '../../../types/ops.js';

/**
 * 折りたたみ時に見せるエントリ数。サイドカラムで隣り合う SLA カード（3〜4 行）と同じ高さに収める
 * ための値で、これを増やすと活動ログだけが縦に伸びてサイドの見た目が崩れる。
 * @graph-connects none
 */
const COLLAPSED_ENTRY_COUNT = 4;

/** トグルが開閉する領域の id（aria-controls で結ぶ）。 @graph-connects none */
const LIST_ID = 'ops-activity-list';

/** OpsActivityCard の props。 */
export interface OpsActivityCardProps {
  /** 表示する活動ログ（古い順。API が起票を先頭に導出して返す）。 */
  activity: OpsActivity[];
}

/**
 * 依頼詳細サイドの活動ログカード。エントリは古い順のまま表示し、`COLLAPSED_ENTRY_COUNT` を超える分は
 * **古い側を畳む**（最新の出来事が常に見えている状態を既定にする）。畳んだ件数はトグルのラベルに出し、
 * 「まだ続きがある」ことが開かなくても分かるようにする。
 * @graph-connects opsActivityTimeLabel [calls] 各エントリの表示時刻を JST へ整形
 */
export function OpsActivityCard({ activity }: OpsActivityCardProps): ReactNode {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = Math.max(0, activity.length - COLLAPSED_ENTRY_COUNT);
  const collapsible = hiddenCount > 0;
  const visible = collapsible && !expanded ? activity.slice(-COLLAPSED_ENTRY_COUNT) : activity;

  return (
    <Card padding="sm">
      <CardHeader title={t('ops.detail.activity')} />
      <CardContent className="space-y-2" id={LIST_ID}>
        {collapsible && (
          <button
            type="button"
            onClick={() => {
              setExpanded(!expanded);
            }}
            aria-expanded={expanded}
            aria-controls={LIST_ID}
            className="text-xs text-[var(--color-accent-primary)] hover:text-[var(--color-accent-hover)]"
          >
            {expanded
              ? t('ops.detail.activityCollapse')
              : t('ops.detail.activityExpand', { count: hiddenCount })}
          </button>
        )}
        {visible.map((a, i) => (
          <div key={`${a.at ?? a.time}-${String(i)}`} className="flex gap-3 text-xs">
            <span className="w-24 shrink-0 whitespace-nowrap text-[var(--color-text-muted)]">
              {opsActivityTimeLabel(a)}
            </span>
            <span className="text-[var(--color-text-secondary)]">
              {a.actor !== null && (
                <span className="font-semibold text-[var(--color-text-primary)]">{a.actor} </span>
              )}
              {a.text}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
